// Maps API error codes to localized, friendly toast messages. The translator is
// registered once at startup (i18n lives in React context; this module doesn't).
type T = (key: string, vars?: Record<string, string | number>) => string
let translate: T | null = null
export function registerErrorT(t: T) { translate = t }

const CODE_KEY: Record<string, string> = {
  TRIAL_EXPIRED: 'errors.trialExpired',
  IMAGE_REJECTED: 'errors.imageRejected',
  QUOTA_EXCEEDED: 'errors.quota',
  DOC_LIMIT: 'errors.docLimit',
  HORSE_LIMIT: 'errors.horseLimit',
  NOT_A_MEMBER: 'errors.notMember',
  UNAUTHORIZED: 'errors.unauthorized',
  RATE_LIMITED: 'errors.rateLimited',
  INVITE_EMAIL_MISMATCH: 'errors.inviteEmailMismatch',
  INVALID_INPUT: 'errors.invalidInput',
}

export function errorMessage(err: unknown): string {
  const e = err as { code?: string; message?: string }
  if (translate && e?.code && CODE_KEY[e.code]) return translate(CODE_KEY[e.code])
  if (e?.message) return e.message
  return translate ? translate('errors.generic') : 'Something went wrong'
}

/**
 * Friendly rewrites of raw Cognito/Amplify auth errors. Non-technical users
 * should never see "PreSignUp failed with error" or "Password did not conform
 * with policy" verbatim — match the raw message and translate.
 */
const AUTH_PATTERNS: Array<[RegExp, string]> = [
  [/incorrect username or password/i, 'authErrors.incorrect'],
  [/not confirmed/i, 'authErrors.notConfirmed'],
  [/already exists|usernameexists/i, 'authErrors.exists'],
  [/password.*(policy|length|conform|constraint)|not long enough/i, 'authErrors.policy'],
  [/invalid verification code|codemismatch/i, 'authErrors.codeMismatch'],
  [/expired/i, 'authErrors.expired'],
  [/attempt limit|too many/i, 'authErrors.attempts'],
  [/user (does not|doesn't) exist|usernotfound/i, 'authErrors.notFound'],
  [/captcha/i, 'authErrors.captcha'],
]

export function authErrorMessage(err: unknown): string {
  const raw = (err as { message?: string })?.message || ''
  if (translate) {
    for (const [re, key] of AUTH_PATTERNS) {
      if (re.test(raw)) return translate(key)
    }
    return translate('authErrors.generic')
  }
  return raw || 'That didn’t work — please try again.'
}
